import { AutoSaveIndicator, BoundSelectField, Css } from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { IncrementalCollectionOp, SaveProgramDataInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageProgramDataV2Query,
  PlanPackageProgramDataV2_PlanPackageFragment,
  PlanPackageProgramDataV2_ProgramDataFragment,
  usePlanPackageProgramDataV2Query,
  useSavePlanPackageProgramDataV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/ProgramDataTab.generated";
import {
  ProgramDataCard,
  ProgramDataFeature,
} from "src/routes/libraries/plan-package-v2/components/program-data/ProgramDataCard";
import { queryResult } from "src/utils";

type ProgramDataTabProps = { planPackageId: string; versionId: string };

export function ProgramDataTab(props: ProgramDataTabProps) {
  const { planPackageId, versionId } = props;
  const query = usePlanPackageProgramDataV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage, enumDetails }) => (
    <DataView planPackage={planPackage} enumDetails={enumDetails} />
  ));
}

type DataViewProps = {
  planPackage: PlanPackageProgramDataV2_PlanPackageFragment;
  enumDetails: PlanPackageProgramDataV2Query["enumDetails"];
};

function DataView(props: DataViewProps) {
  const { planPackage, enumDetails } = props;
  const { programData, minProgramData: min, maxProgramData: max } = planPackage;
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageProgramDataV2Mutation();

  const formState = useFormState({
    config: programDataConfig,
    init: { input: planPackage, map: mapToForm },
    autoSave: async (state) => {
      const { data } = await save({
        variables: {
          input: {
            id: planPackage.id,
            programDatas: [
              {
                id: programData.readyPlanProgramData?.id,
                op: IncrementalCollectionOp.Include,
                programData: state.changedValue,
              },
            ],
          },
        },
      });
      const saved = data?.savePlanPackage.planPackage;
      if (saved && saved.version.id !== planPackage.version.id) {
        // Saving a published version bumped us to a new draft, so clear dirty fields before navigating to it.
        formState.update(saved);
        navigateToRpav(saved.version.id);
      }
    },
  });

  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const feature = (label: string, field: keyof ProgramDataNumerics): ProgramDataFeature => ({
    label,
    base: formState[field],
    min: min[field],
    max: max[field],
  });

  return (
    <div css={Css.p3.df.fdc.gap3.$}>
      <div css={Css.df.aic.jcsb.$}>
        <div>
          <h2 css={Css.xl.$}>Program Data</h2>
          <p css={Css.pt1.$}>
            Enter base program data and the system will calculate the min and max values that can be configured with
            options.
          </p>
        </div>
        <div css={Css.pr1.$}>
          <AutoSaveIndicator />
        </div>
      </div>
      <div css={Css.df.aifs.gap4.$}>
        <div css={Css.fg1.df.fdc.gap3.$}>
          <ProgramDataCard
            title="General"
            icon="house"
            features={[feature("Stories", "stories")]}
            disabled={disabled}
          />
          <ProgramDataCard
            title="Bedrooms"
            icon="bed"
            enumField={
              <BoundSelectField
                fullWidth
                label="Primary Bedroom Location"
                field={formState.primaryBedroom}
                options={enumDetails.programDataPrimaryBedroomConfig.map(({ code, name }) => ({ id: code, name }))}
                disabled={disabled}
              />
            }
            features={[
              feature("Bedrooms", "bedrooms"),
              feature("Closets in Primary Suite", "closetsInPrimarySuite"),
              feature("First Floor Bedrooms", "firstFloorBedrooms"),
            ]}
            disabled={disabled}
          />
          <ProgramDataCard
            title="Bathroom"
            icon="bath"
            features={[feature("Full Baths", "fullBaths"), feature("Half Baths", "halfBaths")]}
            disabled={disabled}
          />
          <ProgramDataCard
            title="Dining"
            icon="dinner"
            features={[feature("Formal", "diningRoom"), feature("Casual", "casualDining")]}
            disabled={disabled}
          />
        </div>
        <div css={Css.fg1.df.fdc.gap3.$}>
          <ProgramDataCard
            title="Entertainment"
            icon="television"
            features={[feature("Media Rooms", "mediaRooms"), feature("Lofts / Game / Flex", "loftGameFlexRooms")]}
            disabled={disabled}
          />
          <ProgramDataCard
            title="Work From Home"
            icon="monitor"
            features={[feature("Offices", "offices"), feature("Workspaces", "workspaces")]}
            disabled={disabled}
          />
          <ProgramDataCard
            title="Garage"
            icon="car"
            enumField={
              <BoundSelectField
                label="Garage Load"
                fullWidth
                field={formState.garageConfiguration}
                options={enumDetails.programDataGarageConfig.map(({ code, name }) => ({ id: code, name }))}
                disabled={disabled}
              />
            }
            features={[
              feature("Attached", "garageAttached"),
              feature("Detached", "garageDetached"),
              feature("Carport", "garagePort"),
            ]}
            disabled={disabled}
          />
          <ProgramDataCard
            title="Basement"
            icon="arrowFromTop"
            enumField={
              <BoundSelectField
                label="Basement Type"
                fullWidth
                field={formState.basementConfig}
                options={enumDetails.programDataBasementConfig.map(({ code, name }) => ({ id: code, name }))}
                disabled={disabled}
              />
            }
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

/** The fields that have a computed min/max, i.e. every numeric field. */
type ProgramDataNumerics = Omit<PlanPackageProgramDataV2_ProgramDataFragment, "__typename" | "id">;

type ProgramDataForm = ProgramDataNumerics &
  Pick<SaveProgramDataInput, "primaryBedroom" | "garageConfiguration" | "basementConfig">;

const programDataConfig: ObjectConfig<ProgramDataForm> = {
  stories: { type: "value" },
  bedrooms: { type: "value" },
  closetsInPrimarySuite: { type: "value" },
  firstFloorBedrooms: { type: "value" },
  fullBaths: { type: "value" },
  halfBaths: { type: "value" },
  diningRoom: { type: "value" },
  casualDining: { type: "value" },
  mediaRooms: { type: "value" },
  loftGameFlexRooms: { type: "value" },
  offices: { type: "value" },
  workspaces: { type: "value" },
  garageAttached: { type: "value" },
  garageDetached: { type: "value" },
  garagePort: { type: "value" },
  primaryBedroom: { type: "value" },
  garageConfiguration: { type: "value" },
  basementConfig: { type: "value" },
};

function mapToForm(planPackage: PlanPackageProgramDataV2_PlanPackageFragment): ProgramDataForm {
  const { id, __typename, readyPlanProgramData, primaryBedroom, garageConfiguration, basementConfig, ...numerics } =
    planPackage.programData;
  return {
    ...numerics,
    primaryBedroom: primaryBedroom?.code,
    garageConfiguration: garageConfiguration?.code,
    basementConfig: basementConfig?.code,
  };
}
